import { TemplatesService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { usePhaseTemplateStore } from '@/store/phaseTemplateStore';
import { useTemplateStore } from '@/store/templateStore';
import { useMilestoneTemplateRulesStore } from './milestoneTemplateRulesStore';
import type {
  CreateMilestoneTemplateRequest,
  MilestoneTemplate,
  MilestoneTemplateUpdate,
  ProjectFunction,
} from '@beeboard/common';

export type MilestoneTemplateStoreState = {
  templates: Record<string, MilestoneTemplate[]>;
};

export const useMilestoneTemplateStore = defineStore('milestoneTemplateStore', {
  state: (): MilestoneTemplateStoreState => ({
    templates: {} as Record<string, MilestoneTemplate[]>,
  }),

  getters: {
    milestone(state) {
      return (templateId: string, milestoneId: string): MilestoneTemplate => {
        const template = state.templates[templateId];
        if (!template) {
          throw new Error('Missing milestone in milestoneStore');
        }
        const milestone = template.find((mil) => mil.id === milestoneId);
        if (!milestone) {
          throw new Error('Missing milestone in milestoneStore');
        }

        return milestone;
      };
    },

    hasMilestone(state) {
      return (templateId: string, milestoneId: string) => {
        const template = state.templates[templateId];

        return !!template?.find((mil) => mil.id === milestoneId);
      };
    },

    isIntermediateMilestone() {
      return (templateId: string, milestoneId: string): boolean => {
        const templateStore = useTemplateStore();
        const template = templateStore.template(templateId);

        return !!template.phases.some((phase) => {
          return phase.intermediate_milestones.some(
            (milestone) => milestone.id === milestoneId,
          );
        });
      };
    },
  },

  actions: {
    write(templateId: string, milestone: MilestoneTemplate) {
      const template = this.getTemplate(templateId);
      const oldMilestone = template.find((mil) => mil.id === milestone.id);
      if (oldMilestone) {
        Object.assign(oldMilestone, milestone);
      } else {
        template.push(milestone);
      }

      return template.find((mil) => mil.id === milestone.id);
    },

    getTemplate(templateId: string) {
      if (!this.templates[templateId]) {
        this.templates[templateId] = [];
      }

      return this.templates[templateId];
    },

    async loadMilestone(templateId: string, milestoneId: string) {
      return TemplatesService.getMilestone(templateId, milestoneId).then(
        ({ milestone }) => this.write(templateId, milestone),
      );
    },

    /**
     * Refetches every cached milestone of the template — approvers, connected
     * Eckpunkte, position — and drops the ones that no longer load. The AI
     * assistant changes them without going through this store (WI #5747).
     */
    async reloadCachedMilestones(templateId: string) {
      const milestoneIds = (this.templates[templateId] ?? []).map(
        ({ id }) => id,
      );

      await Promise.all(
        milestoneIds.map((milestoneId) =>
          this.loadMilestone(templateId, milestoneId).catch(() => {
            this.templates[templateId] = this.getTemplate(templateId).filter(
              ({ id }) => id !== milestoneId,
            );
          }),
        ),
      );
    },

    async add(
      templateId: string,
      phaseId: string,
      payload: CreateMilestoneTemplateRequest,
    ) {
      const errors = useMilestoneTemplateRulesStore().checkAddMilestone(
        templateId,
        phaseId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const { milestone } = await TemplatesService.addPhaseMilestone(
        templateId,
        phaseId,
        payload,
      );
      this.write(templateId, milestone);
      const phase = usePhaseTemplateStore().phase(templateId, phaseId);
      const summary = Object.assign({}, milestone, {
        required_activities: [] as string[],
        activities: [],
      });
      phase.intermediate_milestones.push(summary);

      return this.milestone(templateId, milestone.id);
    },

    async remove(templateId: string, milestoneId: string) {
      const errors = useMilestoneTemplateRulesStore().checkRemoveMilestone(
        templateId,
        milestoneId,
      );
      if (errors) {
        throw errors;
      }
      const template = useTemplateStore().template(templateId);
      const phase = template.phases.find((phase) => {
        return phase.intermediate_milestones.some(
          (milestone) => milestone.id === milestoneId,
        );
      });
      if (!phase) {
        throw new Error('Missing phase removing milestone');
      }
      await TemplatesService.deleteMilestone(templateId, milestoneId);
      const milestones = this.getTemplate(templateId);
      this.templates[templateId] = milestones.filter(
        (m) => m.id !== milestoneId,
      );
      phase.intermediate_milestones = phase.intermediate_milestones.filter(
        ({ id }) => id !== milestoneId,
      );
    },

    async update(
      templateId: string,
      milestoneId: string,
      payload: MilestoneTemplateUpdate,
    ) {
      const errors = useMilestoneTemplateRulesStore().checkUpdateMilestone(
        templateId,
        milestoneId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const { milestone } = await TemplatesService.updateMilestone(
        templateId,
        milestoneId,
        payload,
      );
      this.write(templateId, milestone);
      const summary = useTemplateStore().milestoneSummary(
        templateId,
        milestoneId,
      );
      Object.assign(summary, milestone);
    },

    async updatePosition(
      templateId: string,
      milestoneId: string,
      position: number,
    ) {
      const errors = useMilestoneTemplateRulesStore().checkUpdateMilestone(
        templateId,
        milestoneId,
        { position },
      );
      if (errors) {
        throw errors;
      }
      const { milestone } = await TemplatesService.updateMilestone(
        templateId,
        milestoneId,
        { position },
      );
      this.write(milestoneId, milestone);
      const templateStore = useTemplateStore();
      if (!templateStore.hasTemplate(templateId)) {
        return;
      }
      const summary = templateStore.milestoneSummary(templateId, milestoneId);
      summary.position = milestone.position;
    },

    async removeApprover(
      templateId: string,
      milestoneId: string,
      approverId: string,
    ) {
      const errors = useMilestoneTemplateRulesStore().checkRemoveApprover(
        templateId,
        milestoneId,
        approverId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.milestoneTemplateRemoveApprover(
        templateId,
        milestoneId,
        approverId,
      );
      const milestone = this.milestone(templateId, milestoneId);
      const approverIndex = milestone.approvers?.findIndex(
        ({ id }) => id === approverId,
      );
      if (approverIndex !== undefined && approverIndex > -1) {
        milestone.approvers?.splice(approverIndex, 1);
      }

      return true;
    },

    async addApprover(
      templateId: string,
      milestoneId: string,
      approverId: string,
    ) {
      const errors = useMilestoneTemplateRulesStore().checkAddApprover(
        templateId,
        milestoneId,
        approverId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.milestoneTemplateAddApprover(
        templateId,
        milestoneId,
        { approver_id: approverId },
      );
      const approver = useTemplateStore().projectFunction(
        templateId,
        approverId,
        // TODO: omit this once ActivityTemplateCriteria page is fixed
      ) as ProjectFunction;
      const milestone = this.milestone(templateId, milestoneId);
      milestone.approvers?.push(approver);
    },
  },
});
